import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { fetchWithCache } from '../lib/fetchWithCache';
import { AgbTimeSeries } from '../lib/types';

interface UseAgbTimeSeriesResult {
  data: AgbTimeSeries | null;
  isLoading: boolean;
  error: string | null;
}

const parseAgb = (text: string): AgbTimeSeries => {
  const rows = parseCsv(text);
  const points = rows
    .map(row => ({
      year: new Date(row['time']).getUTCFullYear(),
      avgDensity: Number(row['avg_agb_density']),
      p25: Number(row['p25_agb_density']),
      p75: Number(row['p75_agb_density']),
      std: Number(row['std_agb_density']),
      validPixels: Number(row['valid_pixels']),
    }))
    .filter(p =>
      Number.isFinite(p.year) &&
      Number.isFinite(p.avgDensity) &&
      Number.isFinite(p.p25) &&
      Number.isFinite(p.p75),
    )
    .sort((a, b) => a.year - b.year);
  return { points, units: 'Mg/ha' };
};

/**
 * Fetches the annual mean AGB density time series for the supply shed
 * defined by (millCode, driveTime). CSV lives at
 * sfi/agb/{mill}_{driveTime}min_agb.csv on S3.
 */
export const useAgbTimeSeries = (
  millCode: string | null,
  driveTimeMinutes: number,
  enabled: boolean = true,
): UseAgbTimeSeriesResult => {
  const [data, setData] = useState<AgbTimeSeries | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.agb(millCode, driveTimeMinutes)}`;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache(url, r => r.text().then(parseAgb))
      .then(parsed => {
        if (cancelled) return;
        setData(parsed);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(`Failed to load AGB time series: ${err.message}`);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTimeMinutes, enabled]);

  return { data, isLoading, error };
};
